import { Injectable, signal } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { UserPreferenceService } from 'src/app/modules/my-profile/profile-preferences/user-preference.service';

/** What a favourite points at: a colleague, or a group conversation. */
export type ChatFavouriteKind = 'person' | 'group';

interface FavouritesState {
  /** People are keyed by `user_id`, so a colleague can be starred before you have ever messaged them. */
  people: string[];
  /** Groups are keyed by `conversation_id` — a group has no other stable identity. */
  groups: string[];
}

const EMPTY: FavouritesState = { people: [], groups: [] };

/**
 * Starred people and groups, pinned to the top of the sidebar and filterable on their own.
 *
 * **Persisted server-side**, in the user's display-preferences record
 * (`GET|POST /users/display-preferences`) under a `chat` key. That is the same record the Project
 * and Lead page settings live in, and the only per-user store the API already has — chat itself
 * has no favourites endpoint yet (asked for in
 * `docs/backend/chat/internal-chat-backend-followups.md` §8).
 *
 * `localStorage` is a **write-through cache, not the store**: it exists so the sidebar paints
 * starred rows in the right order on the first frame, before the GET resolves. Clearing site data
 * loses nothing — the next load reads the stars back from the server.
 *
 * Saving is optimistic. The star flips immediately, the request follows, and a failure rolls the
 * signal back and says so, because a star that silently un-stars itself on the next login is worse
 * than one that refuses to be set.
 */
@Injectable({ providedIn: 'root' })
export class ChatFavouritesService {
  /** Read by {@link ChatStore}; every mutation replaces it so computeds re-run. */
  readonly favourites = signal<FavouritesState>(EMPTY);

  /** Set when a save failed, so the UI can say the star did not stick. */
  readonly lastError = signal<string>('');

  constructor(
    private readonly api: ApiService,
    private readonly preferences: UserPreferenceService
  ) {
    // Cache first so the first paint is already correct, then the server has the final word.
    this.favourites.set(this.readCache());
  }

  /**
   * Pull the stored favourites. Called when chat initialises — including after a re-login, which
   * is why it deliberately re-reads rather than latching on the first call.
   *
   * The preferences record is the source of truth, so whatever it says replaces the cache —
   * including "nothing", which is what a user who has never starred anything should see on a
   * borrowed machine.
   */
  load(): void {
    this.preferences.getPreferences().subscribe({
      next: prefs => {
        const state: FavouritesState = {
          people: prefs?.chat?.favouritePeople ?? [],
          groups: prefs?.chat?.favouriteGroups ?? [],
        };
        this.favourites.set(state);
        this.writeCache(state);
      },
      // `getPreferences()` already falls back to its own cache and never throws, but if it ever
      // does, the stars we painted from cache stay on screen rather than vanishing.
      error: () => undefined,
    });
  }

  isFavourite(kind: ChatFavouriteKind, id: string): boolean {
    if (!id) {
      return false;
    }
    const current = this.favourites();
    return kind === 'person' ? current.people.includes(id) : current.groups.includes(id);
  }

  /** Star ⇄ unstar. Returns the new state so a caller can toast "Added to favourites". */
  toggle(kind: ChatFavouriteKind, id: string): boolean {
    if (!id) {
      return false;
    }
    const previous = this.favourites();
    const list = kind === 'person' ? previous.people : previous.groups;
    const next = list.includes(id) ? list.filter(entry => entry !== id) : [...list, id];
    const state = kind === 'person' ? { ...previous, people: next } : { ...previous, groups: next };

    this.apply(state, previous);
    return next.includes(id);
  }

  /** A group that has been deleted or left should not keep a dead star behind it. */
  forget(kind: ChatFavouriteKind, id: string): void {
    if (this.isFavourite(kind, id)) {
      this.toggle(kind, id);
    }
  }

  /**
   * Optimistic write: signal and cache first, server second, rollback on failure.
   *
   * The whole preferences document goes up, because that is what the endpoint takes — so it is
   * built from the freshest record the preference service holds rather than from a stale copy,
   * or a star would revert someone's Project page settings to whatever they were at page load.
   */
  private apply(state: FavouritesState, previous: FavouritesState): void {
    this.favourites.set(state);
    this.writeCache(state);
    this.lastError.set('');

    const current = this.preferences.getCachedPreferences();
    const payload = {
      ...current,
      user_id: this.api.getUserId() || current.user_id,
      chat: { favouritePeople: state.people, favouriteGroups: state.groups },
    };

    this.preferences.savePreferences(payload).subscribe({
      next: result => {
        // A backend that does not know the `chat` key would drop it and hand back a record
        // without it — the stars would then quietly disappear at the next login. Better to find
        // out here, loudly, than in a bug report a week later.
        if (!result?.data?.chat) {
          console.warn('[chat] Favourites were saved but came back without the `chat` key — the API may be discarding it.');
        }
      },
      error: () => {
        this.favourites.set(previous);
        this.writeCache(previous);
        this.lastError.set('Your favourites could not be saved. Please try again.');
      },
    });
  }

  /** Keyed by user, so two accounts on one machine cannot inherit each other's stars. */
  private get cacheKey(): string {
    return `remmi.chat.favourites.${this.api.getUserId() || 'anon'}`;
  }

  private readCache(): FavouritesState {
    try {
      const raw = localStorage.getItem(this.cacheKey);
      if (!raw) {
        return EMPTY;
      }
      const parsed = JSON.parse(raw) as Partial<FavouritesState>;
      return {
        people: Array.isArray(parsed?.people) ? parsed.people.filter(id => typeof id === 'string') : [],
        groups: Array.isArray(parsed?.groups) ? parsed.groups.filter(id => typeof id === 'string') : [],
      };
    } catch {
      // Corrupt or blocked storage — start empty; the server load will fill it in.
      return EMPTY;
    }
  }

  private writeCache(state: FavouritesState): void {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(state));
    } catch {
      // Quota/private mode — irrelevant, the server copy is the real one.
    }
  }
}
